import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Grid,
  Divider,
  Skeleton,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import notifiers from "global/constants/NotificationConstants";
import QRCode from "qrcode";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { handleErrorMessage, openErrorNotification } from "helpers/methods";
import { fetchQRCodeForMFA, fetchSecretKeyForMFA } from "../SettingsService";

interface CustomProps {
  handleDialogClose: Function;
  visible: boolean;
}

const Viewcode = (props: CustomProps) => {
  const [qr, setQR] = useState<string>();
  const [keyCopied, setKeyCopied] = useState<boolean>(false);
  const [secretKey, setSecretKey] = useState<string>("");
  const [loader, setLoader] = useState<boolean>(false);

  useEffect(() => {
    if (props.visible) {
      makeQRCode();
      fetchSecretKey();
    }
  }, [props.visible]);

  const fetchSecretKey = async () => {
    try {
      setLoader(true);
      const response = await fetchSecretKeyForMFA();
      if (!response) {
        throw new Error("Failed to fetch pre-auth secret key");
      }
      setSecretKey(response.match(/.{1,6}/g)?.join("-") || response);
      setLoader(false);
    } catch (error: any) {
      setLoader(false);
      handleErrorMessage(error);
    }
  };

  const copyToClipBoard = async () => {
    await navigator.clipboard.writeText(secretKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const generateQRCodeImage = async (url: string) => {
    try {
      const qrCodeImage = await QRCode.toDataURL(url);
      setQR(qrCodeImage);
    } catch (error: any) {
      openErrorNotification(error);
    }
  };

  const makeQRCode = async () => {
    try {
      const response = await fetchQRCodeForMFA();
      await generateQRCodeImage(response.message);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const getBody = () => {
    return (
      <Stack direction="column" spacing={2} pt={2} textAlign="center">
        <Box>
          {loader ? (
            <>
              <Skeleton
                variant="text"
                width={220}
                height={40}
                sx={{ mx: "auto" }}
              />
              <Skeleton
                variant="text"
                width={280}
                height={20}
                sx={{ mx: "auto", mt: 1 }}
              />
            </>
          ) : (
            <>
              <Typography variant="h4" gutterBottom>
                Keep your account secure
              </Typography>
              <Typography variant="body2">
                Set up the multi-factor authentication for your account.
              </Typography>
            </>
          )}
        </Box>
        <Stack
          alignItems="center"
          sx={{ border: "0.5px solid #E9E9E9", borderRadius: "20px", py: 2 }}
        >
          {loader ? (
            <Skeleton variant="rectangular" width={150} height={150} />
          ) : (
            <img src={qr} height={150} width={150} />
          )}
        </Stack>
        <Divider sx={{ borderColor: "#E9E9E9" }}>
          <Typography textAlign="center" variant="body2">
            OR enter the code manually
          </Typography>
        </Divider>
        {secretKey && (
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              overflowWrap: "break-word",
              wordBreak: "break-all",
              padding: "10px",
            }}
          >
            {loader ? (
              <Skeleton variant="rectangular" width="100%" height={40} />
            ) : (
              <Typography
                sx={{
                  wordWrap: "break-word",
                  fontSize: "14px",
                  textAlign: "center",
                  border: "1px solid #E9E9E9",
                  borderRadius: "8px",
                  p: 1,
                  width: "100%",
                }}
              >
                {secretKey}
              </Typography>
            )}

            {loader ? (
              <Skeleton variant="circular" width={40} height={40} />
            ) : (
              <Typography
                onClick={copyToClipBoard}
                sx={{
                  border: "1px solid #E9E9E9",
                  borderRadius: "8px",
                  p: 1,
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.1)",
                  },
                }}
              >
                {keyCopied ? (
                  <Tooltip title="Copied">
                    <DoneAllIcon sx={{ fontSize: "18px" }} />
                  </Tooltip>
                ) : (
                  <ContentCopyIcon sx={{ fontSize: "18px" }} />
                )}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={props.handleDialogClose}
        isDialogOpen={props.visible}
        body={getBody()}
        maxWidth="xs"
        closable
        closeButtonVisibility
      />
    </>
  );
};

export default Viewcode;
